import { ArrowBack, ArrowForward, KeyboardArrowDown, Visibility } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuItem as MuiMenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import ContainerPainel from "components/container_painel";
import CustomInputTextField from "components/shared/custom_input_textfield";
import { useAuth } from 'contexts/auth/auth_context';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import { CuffaCollectionStatusEnum } from 'interfaces/cuffa.interface';
import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import useCuffaListStore from "stores/cuffa/cuffaList.store";
import { useInstitutionStore } from "stores/institution.store";
import { CuffaAPI } from 'api/cuffa/cuffa.api';


export const getStatusColor = (status: string) => {
  switch (status) {
    case CuffaCollectionStatusEnum.EM_COLETA_RASCUNHO:
      return 'default';
    case CuffaCollectionStatusEnum.EM_COLETA_INCOMPLETO:
      return 'warning';
    case CuffaCollectionStatusEnum.SINCRONIZADO:
      return 'info';
    case CuffaCollectionStatusEnum.VALIDADO_PELO_MUNICIPIO:
      return 'primary';
    case CuffaCollectionStatusEnum.ENVIADO_AO_ESTADO:
      return 'secondary';
    case CuffaCollectionStatusEnum.DEVOLVIDO_PELO_ESTADO:
      return 'error';
    case CuffaCollectionStatusEnum.PROCESSO_FINALIZADO:
      return 'success';
    default:
      return 'default';
  }
};

export default function CuffaListar() {
  const { institution, getInstitution } = useInstitutionStore();
  const {
    cuffaList,
    getCuffaList,
    setCuffaFilters,
    cuffaFilters,
    cuffaPagination,
    switchPage,
    clearFiltersAndGetCuffaList
  } = useCuffaListStore();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStateManagerOrAdmin = [UserPermissionEnum.STATE_MANAGER, UserPermissionEnum.ADMIN].includes(user?.permission.name as UserPermissionEnum);
  const isMunicipalityManager = user?.permission.name === UserPermissionEnum.MUNICIPAL_MANAGER;

  // Estados para o menu dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  // Estados para o modal de devolução ao município
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnJustification, setReturnJustification] = useState('');
  const [isReturning, setIsReturning] = useState(false);

  // Estados para Snackbar de feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSearch = () => {
    // Reset para primeira página ao pesquisar
    switchPage(1);
  }

  const handleClear = () => {
    clearFiltersAndGetCuffaList();
  }

  // Funções para o menu dropdown
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    console.log('row', row);
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleAction = (action: string) => {
    if (action === 'ver') {
      navigate(`${ROUTES.IDENTIFICACAO_FAMILIAR_CUFFA_VISUALIZAR.replace(':uuid', selectedRow.uuid)}`);
      handleMenuClose();
      return;
    }

    if (action === 'devolver_ao_municipio') {
      // Open return modal but keep selectedRow available for the modal flow.
      setIsReturnModalOpen(true);
      // close menu (anchor) but do not clear selectedRow
      setAnchorEl(null);
      return;
    }

    handleMenuClose();
    // Aqui você pode implementar as ações específicas
  };


  const handleCloseReturnModal = () => {
    setIsReturnModalOpen(false);
    setReturnJustification('');
  };

  const handleReturnToMunicipality = async () => {
    if (!returnJustification.trim()) {
      showSnackbar('É obrigatório informar a justificativa para devolução ao município.', 'error');
      return;
    }

    setIsReturning(true);
    try {
      const payload = {
        justification: returnJustification.trim(),
        data_hora: new Date().toISOString(),
        gerente_estadual: user?.name ?? 'Usuário não identificado'
      };

      const response = await CuffaAPI.returnCollectionToMunicipality(selectedRow.uuid, payload);

      if (response && response.Status === 'ERRO') {
        const errorMsg = response.dados?.message || response.Mensagem || 'Erro ao devolver coleta ao município. Tente novamente.';
        showSnackbar(errorMsg, 'error');
        return;
      }

      // Atualizar a lista após a devolução
      getCuffaList();

      // Fechar modal e mostrar mensagem de sucesso
      handleCloseReturnModal();
      showSnackbar('Coleta devolvida ao município com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao devolver coleta ao município:', error);
      showSnackbar('Erro ao devolver coleta ao município. Tente novamente.', 'error');
    } finally {
      setIsReturning(false);
    }
  };

  // Funções de paginação
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    switchPage(page);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<string>) => {
    const newTake = event.target.value;
    const { setCuffaPagination } = useCuffaListStore.getState();
    setCuffaPagination({
      ...cuffaPagination,
      take: newTake === 'all' ? cuffaPagination.total : Number(newTake),
      currentPage: 1
    });
    // Buscar dados com a nova paginação
    const { getCuffaList } = useCuffaListStore.getState();
    getCuffaList();
  };

  useEffect(() => {
    getInstitution({
      page: '1',
      take: '400'
    });
    getCuffaList();
  }, []);

  const totalPages = cuffaPagination.take === cuffaPagination.total ? 1 : Math.ceil(cuffaPagination.total / cuffaPagination.take);

  return (
    <ContainerPainel pagina="Listar Coletas CUFFA" showBackButton={true}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Lista de Coletas CUFFA
        </Typography>

        {/* Filtros */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'white' }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
            Filtros de Pesquisa
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Autocomplete
              disablePortal
              options={institution.map(item => {
                return {
                  label: `${item.address.municipality} - ${item.name}`,
                  uuid: item.uuid
                }
              })}
              sx={{ width: '100%' }}
              value={institution.find(item => item.uuid === cuffaFilters.institution_uuid)?.address.municipality ? {
                label: `${institution.find(item => item.uuid === cuffaFilters.institution_uuid)?.address.municipality} - ${institution.find(item => item.uuid === cuffaFilters.institution_uuid)?.name}`,
                uuid: cuffaFilters.institution_uuid
              } : null}
              renderInput={(params) => <TextField {...params} label="Município" />}
              onChange={(e, value) => {
                setCuffaFilters({ ...cuffaFilters, institution_uuid: value?.uuid ?? '' });
                getCuffaList();
              }}
            />

            <Box sx={{ minWidth: 250, width: '100%', display: 'flex', gap: 2 }}>
              <CustomInputTextField
                value={cuffaFilters.family_unit_name ?? ''}
                onChange={(e) => setCuffaFilters({ ...cuffaFilters, family_unit_name: e.target.value })}
                label="Nome da Unidade Familiar"
                type="text"
                inputProps={{}}
                error={{}}
                helperText={{}}
                fullWidth
                margin="dense"
                required={false}
              />

              <CustomInputTextField
                value={cuffaFilters.cpf ?? ''}
                onChange={(e) => setCuffaFilters({ ...cuffaFilters, cpf: e.target.value })}
                label="CPF"
                type="text"
                inputProps={{}}
                error={{}}
                helperText={{}}
                fullWidth
                margin="dense"
                required={false}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  sx={{
                    minWidth: 120
                  }}
                >
                  Pesquisar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClear}
                  sx={{
                    minWidth: 120
                  }}
                >
                  Limpar
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>

        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Município</strong></TableCell>
                <TableCell><strong>Nome da Unidade Familiar</strong></TableCell>
                <TableCell><strong>Nome da Comunidade</strong></TableCell>
                <TableCell><strong>CPF / Nome do Informante</strong></TableCell>
                <TableCell><strong>Ano Referência</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(cuffaList || []).map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>{row.institution.address.municipality}</TableCell>
                  <TableCell>{row.family_unit_name}</TableCell>
                  <TableCell>{row.community_name}</TableCell>
                  <TableCell>
                    <Box display='flex' flexDirection='column' gap={1}>
                      <Typography variant="body2">{row.informant_full_name}</Typography>
                      <Typography variant="body2">{row.informant_cpf}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.reference_year}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      color={getStatusColor(row.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(event) => handleMenuOpen(event, row)}
                      endIcon={<KeyboardArrowDown />}
                      sx={{ minWidth: 'auto' }}
                    >
                      Ações
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação similar ao manage_user.tsx */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
            paddingBottom: '20px',
          }}
        >
          <Pagination
            count={totalPages}
            page={cuffaPagination.currentPage}
            color='primary'
            onChange={handlePageChange}
          />
          <Box display='flex' alignItems='center' gap={1} sx={{ marginLeft: '20px' }}>
            <Typography variant='body2'>Itens por página:</Typography>
            <Select
              value={cuffaPagination.take === cuffaPagination.total ? 'all' : String(cuffaPagination.take)}
              onChange={handleChangeRowsPerPage}
              size='small'
              renderValue={(selected) => {
                if (selected === 'all') return 'Todos';
                return selected;
              }}
            >
              {[10, 25, 50, 100].map((option) => (
                <MuiMenuItem key={option} value={option}>
                  {option}
                </MuiMenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Menu Dropdown para Ações */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleAction('ver')}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver</ListItemText>
          </MenuItem>

          {
            isStateManagerOrAdmin &&
            selectedRow?.status === CuffaCollectionStatusEnum.ENVIADO_AO_ESTADO &&
            !selectedRow?.has_approval && (
              <MenuItem onClick={() => handleAction('devolver_ao_municipio')}>
                <ListItemIcon>
                  <ArrowBack fontSize="small" />
                </ListItemIcon>
                <ListItemText>Devolver ao Município</ListItemText>
              </MenuItem>
            )
          }


          {
            isMunicipalityManager && selectedRow?.status !== CuffaCollectionStatusEnum.ENVIADO_AO_ESTADO && (
              <MenuItem onClick={() => handleAction('enviar_ao_estado')}>
                <ListItemIcon>
                  <ArrowForward fontSize="small" />
                </ListItemIcon>
                <ListItemText>Enviar ao Estado</ListItemText>
              </MenuItem>
            )
          }


        </Menu>

        {/* Modal de Devolução ao Município */}
        <Dialog
          open={isReturnModalOpen}
          onClose={handleCloseReturnModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Devolver Coleta ao Município
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2, mt: 1 }}>
              Descreva a justificativa ou orientação para devolução ao município:
            </Typography>
            <TextField
              autoFocus
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              label="Descreva a justificativa ou orientação para devolução ao município:"
              value={returnJustification}
              onChange={(e) => setReturnJustification(e.target.value)}
              placeholder="Descreva os motivos da devolução e as orientações necessárias..."
              required
              error={returnJustification.length === 0}
              helperText={returnJustification.length === 0 ? 'Campo obrigatório' : ''}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseReturnModal}
              disabled={isReturning}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleReturnToMunicipality}
              variant="contained"
              color="primary"
              disabled={isReturning || !returnJustification.trim()}
            >
              {isReturning ? 'Confirmando...' : 'Confirmar devolução'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ContainerPainel >
  )
}
